import React, { useState } from 'react';
import { User } from 'store/user/types';
import { useTranslation } from 'react-i18next';
import { useUserThunkDispatch } from 'hooks';
import { userActions } from 'store/user';
import { ReactComponent as SettingsIcon } from 'assets/icons_3/settings.svg';
import { ReactComponent as LogOutIcon } from 'assets/icons_3/log-out.svg';
import OverlayTrigger from 'components/UI_kit_3/Popover/OverlayTrigger/OverlayTrigger';
import Popover from 'components/UI_kit_3/Popover/Popover';
import DropdownItem from 'components/UI_kit_3/Popover/DropdownItem/DropdownItem';
import SwitchButton from 'components/UI_kit_3/SwitchButton/SwitchButton';
import classNames from 'classnames';
import Modal from 'components/UI_kit_3/Modal/Modal';
import Button from 'components/UI_kit_3/Button/Button';
import Tooltip from 'components/UI_kit_3/Popover/Tooltip/Tooltip';
import styles from './UserCard.module.scss';

interface IUserCardProps {
  user: User
  className?: string
}

const UserCard: React.FC<IUserCardProps> = ({ className, user }) => {
  const [t, i18n] = useTranslation();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [open, setOpen] = useState(false);
  const userDispatch = useUserThunkDispatch();
  const handleLogOut = async () => {
    userDispatch(userActions.signOut);
  };
  const handleSelect = (value: string | null) => {
    if (value) {
      i18n.changeLanguage(value);
      localStorage.setItem('pdlang', value);
      userDispatch(userActions.setApiLanguage(value));
    }
  };

  React.useEffect(() => {
    if (user.language && user.language !== i18n.language) {
      i18n.changeLanguage(user.language);
      localStorage.setItem('pdlang', user.language);
    }

    if (user.token && user.language === null) {
      userDispatch(userActions.setApiLanguage(i18n.language));
    }
  }, [user]);

  const userBadge = user.fullName.split(' ').slice(0, 2).map((name) => name.slice(0, 1)).join('');

  return (
    <>
      <OverlayTrigger
        className={styles.userCard}
        position="bottom-right"
        open={open}
        onOpenChange={setOpen}
        overlay={(
          <Popover className={styles.menu} qa-element="user-menu">
            <div className={styles.header}>
              <div className={styles.user}>
                <span className={styles.userBadge}>{userBadge}</span>
                <span className={styles.onlineBadge} />
              </div>
              <div>
                <Tooltip active={user.fullName.length > 25} text={user.fullName} position="top-right" arrow>
                  <div className={styles.name}>{user.fullName}</div>
                </Tooltip>
                <div className={styles.login}>{user.email}</div>
              </div>
            </div>
            <SwitchButton
              className={styles.lang}
              value={i18n.language}
              values={[
                {
                  value: 'ru',
                  title: 'RU',
                },
                {
                  value: 'en',
                  title: 'EN',
                },
              ]}
              onChange={handleSelect}
              qaElement="lang-switcher"
            />
            <div>
              <DropdownItem
                to="/settings/account"
                qa-element="account-settings"
                onClick={() => setOpen(false)}
              >
                <SettingsIcon />
                {t('navigation.settings')}
              </DropdownItem>
              <div className={styles.divider} />
              <DropdownItem className={styles.logOut} onClick={() => setShowLogoutConfirm(true)} qa-element="sign-out">
                <LogOutIcon />
                {t('auth.signout')}
              </DropdownItem>
            </div>
          </Popover>
    )}
      >
        <button className={classNames(styles.toggle, className)} type="button" qa-element="user-menu-toggle" onClick={() => setOpen(true)}>
          <div className={styles.user}>
            <span className={styles.userBadge}>{userBadge}</span>
            <span className={styles.onlineBadge} />
          </div>
        </button>
      </OverlayTrigger>
      <Modal show={showLogoutConfirm} onHide={() => setShowLogoutConfirm(false)}>
        <Modal.Header closeButton>
          {t('auth.logout_confirm')}
        </Modal.Header>
        <Modal.Body />
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLogoutConfirm(false)} qa-element="cancel-logout">{t('common.cancel')}</Button>
          <Button variant="danger" onClick={handleLogOut} qa-element="confirm-logout">{t('auth.signout')}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserCard;
